import { useId, useRef, useState } from "react";
import { Form, Link, redirect, useActionData, useNavigation } from "react-router";
import type { ActionFunctionArgs } from "react-router";
import { ImagePlus } from "lucide-react";
import { ApiError } from "@repo/api-client";
import type { Feedback } from "@repo/shared-types";
import { api } from "@/lib/api";
import { DEFAULT_KIND, KINDS, MAX_FEEDBACK } from "@/lib/feedback";
import { looksLikeAPicture, uploadPicture } from "@/lib/images";
import { formError } from "@/lib/session";
import { toast } from "@/lib/toast";
import { PageTitle } from "@/components/brand";
import { ErrorNote, PictureButton, SegmentedControl, useFormProblem } from "@/components/form";
import { LeaveGuard } from "@/components/leave-guard";
import { BackLink } from "@/components/navigation";
import { Button } from "@/components/ui/button";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Textarea } from "@/components/ui/textarea";
import { useSession } from "@/routes/root";
import { feedbackSession } from "@/routes/feedback";

/**
 * Sending feedback (0091): what kind it is, the words, and — where pictures
 * are kept — a screenshot behind "+ Add screenshot". It is stored, shown to
 * every tester and emailed to the people building the app with the sender's
 * username and address, five a day per account.
 *
 * The screenshot goes up after the feedback is stored, from this action. One
 * that fails does not lose the feedback: the sender lands on it, told the
 * picture did not go.
 */

export async function newFeedbackLoader({ request }: { request: Request }) {
  await feedbackSession(request);
  return null;
}

type ActionResult = { error?: string; field?: string };

export async function newFeedbackAction({ request }: ActionFunctionArgs): Promise<ActionResult | Response> {
  const form = await request.formData();
  const message = String(form.get("message") ?? "");
  const kind = String(form.get("kind") ?? "") || DEFAULT_KIND;
  if (message.trim() === "") return { error: "Write your feedback first.", field: "message" };

  let sent: Feedback;
  try {
    sent = await api.feedback.send({ message, kind });
  } catch (error) {
    // Five a day per account: said in the page's words, whatever the api's.
    if (error instanceof ApiError && error.status === 429) {
      return { error: "You have sent a lot of feedback today. Try again tomorrow." };
    }
    return formError(error, "Your feedback could not be sent. Try again in a moment.");
  }

  const file = form.get("file");
  if (looksLikeAPicture(file)) {
    try {
      await uploadPicture({
        start: () => api.feedback.picture.start(sent.id),
        confirm: (mediaId) => api.space("me").media.uploaded(mediaId),
        file,
      });
    } catch {
      // The feedback is stored: land on it, where the page says the picture did not go.
      return redirect(`/feedback/${encodeURIComponent(sent.id)}?picture=failed`);
    }
  }
  toast("Thanks — sent.");
  return redirect("/feedback");
}

export default function NewFeedback() {
  const session = useSession();
  const result = useActionData() as ActionResult | undefined;
  const sending = useNavigation().state === "submitting";
  const form = useRef<HTMLFormElement>(null);
  const shown = useFormProblem(result, form);
  const [kind, setKind] = useState<string>(DEFAULT_KIND);
  // The screenshot's box exists only once asked for, so closed it sends nothing.
  const [adding, setAdding] = useState(false);
  const [chosen, setChosen] = useState("");
  const id = useId();
  const problem = shown.problem?.field === "message" ? shown.problem.error : undefined;

  return (
    <section className="space-y-6">
      <PageTitle of={["Send feedback", "Feedback"]} />
      <div className="space-y-2">
        <BackLink to="/feedback">Feedback</BackLink>
        <h1 className="page-title">Send feedback</h1>
        <p className="text-muted-foreground">
          What's working, what isn't, what you'd like. It goes to the people building the app, with your
          username and email address, so they can reply. Every tester sees it on the board, with your username.
        </p>
      </div>

      <Form
        ref={form}
        method="post"
        encType={session?.images ? "multipart/form-data" : undefined}
        noValidate
        onChange={shown.onChange}
        className="space-y-6"
      >
        {shown.general && <ErrorNote message={shown.general} />}
        <SegmentedControl
          label="Kind"
          name="kind"
          options={KINDS.map(({ value, label }) => ({ value, label }))}
          value={kind}
          onChange={setKind}
        />
        <Field data-invalid={problem ? true : undefined}>
          <FieldLabel htmlFor={id}>Your feedback</FieldLabel>
          <Textarea
            id={id}
            name="message"
            maxLength={MAX_FEEDBACK}
            rows={8}
            aria-invalid={problem ? true : undefined}
            className="min-h-48"
          />
          {problem && <FieldError>{problem}</FieldError>}
        </Field>

        {session?.images &&
          (adding ? (
            <div className="flex flex-wrap items-center gap-2">
              <PictureButton name="file" onChange={(change) => setChosen(change.target.files?.[0]?.name ?? "")}>
                <ImagePlus aria-hidden="true" />
                {chosen ? "Replace screenshot" : "Choose a screenshot"}
              </PictureButton>
              {chosen && <span className="min-w-0 truncate text-sm text-muted-foreground">{chosen}</span>}
              <Button
                type="button"
                variant="outline"
                aria-label="Remove the screenshot"
                onClick={() => {
                  setAdding(false);
                  setChosen("");
                }}
              >
                Remove
              </Button>
            </div>
          ) : (
            <Button type="button" variant="outline" onClick={() => setAdding(true)}>
              + Add screenshot
            </Button>
          ))}

        <div className="flex justify-end gap-2">
          <Button asChild variant="outline" size="lg">
            <Link to="/feedback">Cancel</Link>
          </Button>
          <Button type="submit" size="lg" disabled={sending}>
            {sending ? "Sending…" : "Send"}
          </Button>
        </div>
        <LeaveGuard form={form} title="Leave without sending?">
          What you typed here is not saved.
        </LeaveGuard>
      </Form>
    </section>
  );
}
